import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useApiErrorHandler from '../hooks/useApiErrorHandler';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import dashboardService from '../services/DashboardService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import ColegioService from '../services/ColegioService';
import { hasOpenAIChatsEnabled, isFeatureEnabled } from '../utils/adminMenu';
import './NewDashboard.css';
import ProfessorMenu, { buildProfessorMenuItems } from './common/menus/ProfessorMenu';

const formatDateTime = (value) => {
  if (!value) return 'Sin registro reciente';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin registro reciente';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const activityIcons = {
  openai: 'fas fa-robot',
  clasico: 'fas fa-brain',
  foro: 'fas fa-comments',
  archivo: 'fas fa-file-alt'
};

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const hasModuloAlumnosEnabled = (flags) => isFeatureEnabled(flags, 'ModuloAlumnos');
const hasAgentesClasicosEnabled = (flags) => isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');

const NewDashboard = () => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleApiError = useApiErrorHandler();
  const isProfesor = isProfessorUser(user);

  const [datosFormateados, setDatosFormateados] = useState(null);
  const [tendencias, setTendencias] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasModuloAlumnos = useMemo(() => hasModuloAlumnosEnabled(featureFlags), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => hasAgentesClasicosEnabled(featureFlags), [featureFlags]);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const menuItems = useMemo(
    () => buildProfessorMenuItems({ hasOpenAIChats, hasAgentesClasicos, hasModuloAlumnos }),
    [hasOpenAIChats, hasAgentesClasicos, hasModuloAlumnos]
  );

  const isDataLoaded = useRef(false);
  const hasLoggedRole = useRef(false);

  useEffect(() => {
    if (user && !hasLoggedRole.current) {
      console.group('[NewDashboard] Debug Rol');
      console.log('Usuario:', user);
      console.log('roleName:', user?.roleName);
      console.log('rol (string):', user?.role);
      console.log('roleId:', user?.roleId);
      console.log('isProfesor flag:', isProfesor);
      console.groupEnd();
      hasLoggedRole.current = true;
    }
  }, [user, isProfesor]);

  useEffect(() => {
    if (authLoading) return;

    if (!isProfesor) {
      setIsLoading(false);
      return;
    }

    if (!isDataLoaded.current) {
      isDataLoaded.current = true;
      cargarEstadisticas();
    }
  }, [isProfesor, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.deniedCreateAssistant) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const cargarEstadisticas = async () => {
    if (authLoading) return;
    if (!isProfesor) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const estadisticasData = await dashboardService.getEstadisticas();
      const datosDashboard = dashboardService.formatearDatosParaGraficos(estadisticasData);
      setDatosFormateados(datosDashboard);

      const tendenciasData = dashboardService.calcularTendencias(datosDashboard);
      setTendencias(tendenciasData);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);

      try {
        handleApiError(error);
      } catch (handledError) {
        setAlert({
          show: true,
          message: handledError.message || error.message || 'Error al cargar las estadísticas del dashboard',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleNewAssistant = () => {
    navigate('/new-assistant');
  };

  const handleMenuNavigate = (item) => {
    if (!item) return;
    setActiveMenuItem(item.id);
    setIsSidebarOpen(false);
    if (item.path) {
      navigate(item.path, item.state ? { state: item.state } : undefined);
    }
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  const resumenPrincipal = datosFormateados?.resumenPrincipal;
  const pulsoHoy = datosFormateados?.pulsoHoy;

  const overviewCards = useMemo(() => {
    if (!resumenPrincipal) {
      return [
        { title: 'Cursos activos', value: '-', icon: 'fas fa-school', color: 'primary', subtitle: 'Sincronizando datos docentes' },
        { title: 'Alumnos únicos', value: '-', icon: 'fas fa-user-graduate', color: 'secondary', subtitle: 'Pendiente de cálculo' },
        { title: 'Materias asignadas', value: '-', icon: 'fas fa-book-open', color: 'success', subtitle: 'Pendiente de cálculo' },
        { title: 'Aulas activas', value: '-', icon: 'fas fa-chalkboard', color: 'accent', subtitle: 'Pendiente de cálculo' }
      ];
    }

    return [
      {
        title: 'Cursos activos',
        value: resumenPrincipal.totalCursos.toString(),
        icon: 'fas fa-school',
        color: 'primary',
        subtitle: `${resumenPrincipal.promedioAlumnosPorCurso} alumnos promedio por curso`
      },
      {
        title: 'Alumnos únicos',
        value: resumenPrincipal.totalAlumnos.toString(),
        icon: 'fas fa-user-graduate',
        color: 'secondary',
        subtitle: `Curso más poblado: ${resumenPrincipal.cursoConMasAlumnos}`
      },
      {
        title: 'Materias asignadas',
        value: resumenPrincipal.totalMaterias.toString(),
        icon: 'fas fa-book-open',
        color: 'success',
        subtitle: `${resumenPrincipal.totalArchivos} archivos disponibles en aulas`
      },
      {
        title: 'Aulas activas',
        value: resumenPrincipal.totalAulas.toString(),
        icon: 'fas fa-chalkboard',
        color: 'accent',
        subtitle: `Más activa: ${resumenPrincipal.aulaMasActiva}`
      }
    ];
  }, [resumenPrincipal]);

  const pulseCards = useMemo(() => {
    if (!resumenPrincipal || !pulsoHoy) {
      return [];
    }

    return [
      {
        title: 'Actividad de hoy',
        value: pulsoHoy.actividadHoy.toString(),
        icon: 'fas fa-wave-square',
        color: 'primary',
        subtitle: `${pulsoHoy.asistentesHoy} interacciones con asistentes hoy`
      },
      {
        title: 'Mensajes de foro',
        value: pulsoHoy.foroHoy.toString(),
        icon: 'fas fa-comments',
        color: 'secondary',
        subtitle: `${resumenPrincipal.totalMensajesForo} acumulados`
      },
      {
        title: 'Archivos subidos hoy',
        value: pulsoHoy.archivosHoy.toString(),
        icon: 'fas fa-file-upload',
        color: 'success',
        subtitle: `${resumenPrincipal.totalArchivos} archivos en total`
      },
      {
        title: 'Respuestas de hoy',
        value: pulsoHoy.respuestasHoy.toString(),
        icon: 'fas fa-reply',
        color: 'accent',
        subtitle: `${resumenPrincipal.totalRespuestasForo} respuestas acumuladas`
      }
    ];
  }, [pulsoHoy, resumenPrincipal]);

  const assistantSummaryCards = useMemo(() => {
    if (!resumenPrincipal) {
      return [];
    }

    const cards = [];

    if (hasOpenAIChats) {
      cards.push({
        title: 'Asistentes OpenAI',
        value: resumenPrincipal.totalAsistentesOpenAI.toString(),
        icon: 'fas fa-robot',
        tone: 'openai',
        subtitle: `${resumenPrincipal.threadsMes} conversaciones del mes`
      });
    }

    if (hasAgentesClasicos) {
      cards.push({
        title: 'Asistente IA',
        value: resumenPrincipal.totalAsistentesClasicos.toString(),
        icon: 'fas fa-brain',
        tone: 'clasico',
        subtitle: `${resumenPrincipal.conversacionesClasicasMes} conversaciones del mes`
      });
    }

    if (hasOpenAIChats || hasAgentesClasicos) {
      cards.push({
        title: 'Asistente más usado',
        value: resumenPrincipal.asistenteMasUsado,
        icon: 'fas fa-crown',
        tone: 'neutral',
        subtitle: `${resumenPrincipal.totalAsistentes} asistentes disponibles en tu panel`
      });
    }

    return cards;
  }, [hasAgentesClasicos, hasOpenAIChats, resumenPrincipal]);

  const visibleAssistantRanking = useMemo(() => {
    const items = datosFormateados?.topAsistentes || [];
    return items.filter((item) => {
      if (item.esClasico) return hasAgentesClasicos;
      return hasOpenAIChats;
    });
  }, [datosFormateados, hasAgentesClasicos, hasOpenAIChats]);

  const visibleActivitySeries = useMemo(() => {
    const baseSeries = [
      { key: 'threads', label: 'OpenAI', tone: 'openai', icon: 'fas fa-robot', visible: hasOpenAIChats },
      { key: 'clasicos', label: 'Asistente IA', tone: 'clasico', icon: 'fas fa-brain', visible: hasAgentesClasicos },
      { key: 'foro', label: 'Foro', tone: 'foro', icon: 'fas fa-comments', visible: true },
      { key: 'archivos', label: 'Archivos', tone: 'archivos', icon: 'fas fa-file-alt', visible: true }
    ];

    return baseSeries.filter((series) => series.visible);
  }, [hasAgentesClasicos, hasOpenAIChats]);

  const dashboardHeadline = useMemo(() => {
    if (!resumenPrincipal) {
      return 'Consolidando métricas del ecosistema docente.';
    }

    const base = `Gestionás ${resumenPrincipal.totalCursos} cursos, ${resumenPrincipal.totalAulas} aulas y ${resumenPrincipal.totalAlumnos} alumnos únicos.`;
    const assistants = [];
    if (hasOpenAIChats) assistants.push(`${resumenPrincipal.totalAsistentesOpenAI} asistentes OpenAI`);
    if (hasAgentesClasicos) assistants.push(`${resumenPrincipal.totalAsistentesClasicos} asistentes IA`);

    return assistants.length > 0
      ? `${base} Además, tenés ${assistants.join(' y ')} activos para acompañar las clases.`
      : base;
  }, [hasAgentesClasicos, hasOpenAIChats, resumenPrincipal]);

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!isProfesor) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Este dashboard está disponible únicamente para usuarios con rol Profesor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`new-dashboard ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}

      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>

        <ProfessorMenu
          activeId={activeMenuItem}
          hasOpenAIChats={hasOpenAIChats}
          hasAgentesClasicos={hasAgentesClasicos}
          hasModuloAlumnos={hasModuloAlumnos}
          onNavigate={handleMenuNavigate}
        />

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || 'Profesor'}</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              {menuItems.find((item) => item.id === activeMenuItem)?.label || 'Dashboard'}
            </h1>
          </div>

          <div className="header-right">
            <SolicitudesDropdown />
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={handleLogout} passwordLabel="Actualizar Password" />
          </div>
        </header>

        <div className="dashboard-content">
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {activeMenuItem === 'dashboard' && (
            <div className="dashboard-overview">
              {isLoading ? (
                <div className="loading-container">
                  <LoadingSpinner message="Obteniendo estadísticas..." />
                </div>
              ) : (
                <>
                  <div className="dashboard-toolbar">
                    <button
                      type="button"
                      className="dashboard-refresh-btn"
                      onClick={cargarEstadisticas}
                      disabled={isLoading}
                    >
                      <i className="fas fa-rotate-right"></i>
                      Actualizar dashboard
                    </button>
                    {hasOpenAIChats && (
                      <button type="button" className="dashboard-shortcut-btn" onClick={handleNewAssistant}>
                        <i className="fas fa-plus"></i>
                        Nuevo Asistente OpenAI
                      </button>
                    )}
                  </div>

                  <section className="dashboard-hero">
                    <div className="hero-copy">
                      <span className="hero-eyebrow">Panel docente</span>
                      <h2>Vista integral de tu actividad académica</h2>
                      <p>{dashboardHeadline}</p>
                      <div className="hero-chips">
                        <span className="hero-chip">
                          <i className="fas fa-users"></i>
                          Curso más poblado: {resumenPrincipal?.cursoConMasAlumnos || 'Sin datos'}
                        </span>
                        <span className="hero-chip">
                          <i className="fas fa-fire"></i>
                          Aula más activa: {resumenPrincipal?.aulaMasActiva || 'Sin datos'}
                        </span>
                        {(hasOpenAIChats || hasAgentesClasicos) && (
                          <span className="hero-chip">
                            <i className="fas fa-crown"></i>
                            Asistente top: {resumenPrincipal?.asistenteMasUsado || 'Sin datos'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="hero-highlight">
                      <span className="hero-highlight-label">Pulso de hoy</span>
                      <strong>{pulsoHoy?.actividadHoy ?? 0}</strong>
                      <p>acciones registradas entre asistentes, foro y archivos</p>
                    </div>
                  </section>

                  <div className="stats-grid">
                    {overviewCards.map((card) => (
                      <div key={card.title} className={`stats-card ${card.color}`}>
                        <div className="card-icon">
                          <i className={card.icon}></i>
                        </div>
                        <div className="card-content">
                          <h3 className="card-value">{card.value}</h3>
                          <p className="card-title">{card.title}</p>
                          {card.subtitle && <p className="card-subtitle">{card.subtitle}</p>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {pulseCards.length > 0 && (
                    <section className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-gauge-high"></i>
                          Pulso del día
                        </h2>
                      </div>
                      <div className="mini-stats-grid">
                        {pulseCards.map((card) => (
                          <div key={card.title} className={`mini-stat-card ${card.color}`}>
                            <div className="mini-stat-top">
                              <span className="mini-stat-icon">
                                <i className={card.icon}></i>
                              </span>
                              <span className="mini-stat-title">{card.title}</span>
                            </div>
                            <strong className="mini-stat-value">{card.value}</strong>
                            <p className="mini-stat-subtitle">{card.subtitle}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="dashboard-section">
                    <div className="section-header section-header-with-legend">
                      <h2 className="section-title">
                        <i className="fas fa-chart-column"></i>
                        Actividad de los últimos 7 días
                      </h2>
                      <div className="activity-legend">
                        {visibleActivitySeries.map((series) => (
                          <span key={series.key} className={`activity-legend-item ${series.tone}`}>
                            <i className={series.icon}></i>
                            {series.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="activity-week-grid">
                      {(datosFormateados?.actividadSemanal || []).map((dia) => (
                        <div key={dia.fechaCompleta} className="activity-day-card">
                          <div className="activity-day-header">
                            <span>{dia.fecha}</span>
                            <strong>{dia.total}</strong>
                          </div>
                          <div className="activity-day-bar">
                            {dia.total > 0 ? (
                              visibleActivitySeries.map((series) => {
                                const value = dia[series.key] || 0;
                                if (!value) return null;
                                const width = Math.max((value / dia.total) * 100, 8);
                                return (
                                  <span
                                    key={`${dia.fechaCompleta}-${series.key}`}
                                    className={`activity-segment ${series.tone}`}
                                    style={{ width: `${width}%` }}
                                  />
                                );
                              })
                            ) : (
                              <span className="activity-empty-bar">Sin actividad</span>
                            )}
                          </div>
                          <div className="activity-day-breakdown">
                            {visibleActivitySeries.map((series) => (
                              <span key={`${series.key}-${dia.fechaCompleta}`} className={`activity-breakdown-item ${series.tone}`}>
                                <i className={series.icon}></i>
                                {dia[series.key] || 0}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <div className="dashboard-split-grid">
                    <section className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-brain"></i>
                          Ecosistema de asistentes
                        </h2>
                      </div>
                      {assistantSummaryCards.length > 0 ? (
                        <>
                          <div className="assistant-cards-grid">
                            {assistantSummaryCards.map((card) => (
                              <div key={card.title} className={`assistant-summary-card ${card.tone}`}>
                                <div className="assistant-summary-icon">
                                  <i className={card.icon}></i>
                                </div>
                                <div>
                                  <span className="assistant-summary-title">{card.title}</span>
                                  <strong className="assistant-summary-value">{card.value}</strong>
                                  <p className="assistant-summary-subtitle">{card.subtitle}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="ranking-list">
                            {visibleAssistantRanking.length > 0 ? (
                              visibleAssistantRanking.map((assistant, index) => (
                                <div key={assistant.id} className="ranking-item">
                                  <div className="ranking-main">
                                    <span className="ranking-badge">{index + 1}</span>
                                    <div>
                                      <h4>{assistant.nombre}</h4>
                                      <p>
                                        {assistant.tipo} · {assistant.esClasico ? `${assistant.conversacionesClasicas} conversaciones` : `${assistant.threads} conversaciones`}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="ranking-bar">
                                    <span
                                      className={`ranking-fill ${assistant.esClasico ? 'clasico' : 'openai'}`}
                                      style={{ width: `${assistant.porcentaje}%` }}
                                    />
                                  </div>
                                  <div className="ranking-side">
                                    <strong>{assistant.interacciones}</strong>
                                    <small>{formatDateTime(assistant.ultimoUso)}</small>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="dashboard-empty-state">
                                <i className="fas fa-robot"></i>
                                <p>No hay asistentes con actividad todavía.</p>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="dashboard-empty-state">
                          <i className="fas fa-toggle-off"></i>
                          <p>En este colegio no hay asistentes habilitados para mostrar en el dashboard.</p>
                        </div>
                      )}
                    </section>

                    <section className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-user-group"></i>
                          Cursos con mayor carga
                        </h2>
                      </div>
                      <div className="ranking-list">
                        {(datosFormateados?.topCursos || []).length > 0 ? (
                          datosFormateados.topCursos.map((curso, index) => (
                            <div key={curso.id} className="ranking-item">
                              <div className="ranking-main">
                                <span className="ranking-badge">{index + 1}</span>
                                <div>
                                  <h4>{curso.nombre}</h4>
                                  <p>{curso.alumnos} alumnos · {curso.materias} materias · {curso.aulas} aulas</p>
                                </div>
                              </div>
                              <div className="ranking-bar">
                                <span className="ranking-fill neutral" style={{ width: `${curso.porcentajeAlumnos}%` }} />
                              </div>
                              <div className="ranking-side">
                                <strong>{curso.alumnos}</strong>
                                <small>{curso.mensajesForo} mensajes de foro</small>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="dashboard-empty-state">
                            <i className="fas fa-school"></i>
                            <p>No hay cursos asignados para mostrar todavía.</p>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>

                  <div className="dashboard-split-grid">
                    <section className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-chalkboard"></i>
                          Aulas con más movimiento
                        </h2>
                      </div>
                      <div className="ranking-list">
                        {(datosFormateados?.topAulas || []).length > 0 ? (
                          datosFormateados.topAulas.map((aula, index) => (
                            <div key={aula.id} className="ranking-item">
                              <div className="ranking-main">
                                <span className="ranking-badge">{index + 1}</span>
                                <div>
                                  <h4>{aula.nombre}</h4>
                                  <p>
                                    {aula.curso} · {aula.materia}{aula.codigoMateria ? ` (${aula.codigoMateria})` : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="ranking-bar">
                                <span className="ranking-fill foro" style={{ width: `${aula.porcentajeActividad}%` }} />
                              </div>
                              <div className="ranking-side">
                                <strong>{aula.scoreActividad}</strong>
                                <small>{aula.archivos} archivos · {aula.mensajesForo} mensajes</small>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="dashboard-empty-state">
                            <i className="fas fa-chalkboard"></i>
                            <p>Todavía no hay aulas con actividad suficiente para rankear.</p>
                          </div>
                        )}
                      </div>
                    </section>

                    <section className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-clock-rotate-left"></i>
                          Actividad reciente
                        </h2>
                      </div>
                      <div className="recent-activity-list">
                        {(datosFormateados?.actividadReciente || []).length > 0 ? (
                          datosFormateados.actividadReciente.map((item, index) => (
                            <div key={`${item.tipo}-${index}`} className="recent-activity-item">
                              <div className={`recent-activity-icon ${item.tipo}`}>
                                <i className={activityIcons[item.tipo] || 'fas fa-circle'}></i>
                              </div>
                              <div className="recent-activity-content">
                                <div className="recent-activity-header">
                                  <strong>{item.titulo}</strong>
                                  <span>{item.fechaFormateada}</span>
                                </div>
                                <p>{item.descripcion}</p>
                                {item.meta && <small>{item.meta}</small>}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="dashboard-empty-state">
                            <i className="fas fa-stream"></i>
                            <p>No hay actividad reciente para mostrar todavía.</p>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>

                  {tendencias && (
                    <section className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-compass"></i>
                          Lecturas rápidas del tablero
                        </h2>
                      </div>
                      <div className="metrics-grid">
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-chart-line"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Promedio diario</h4>
                            <span className="metric-value">{tendencias.promedioDiario}</span>
                            <small>acciones promedio por día</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-calendar-day"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Día más activo</h4>
                            <span className="metric-value">{tendencias.diaMasActivo.fecha}</span>
                            <small>{tendencias.diaMasActivo.total} acciones registradas</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-bullseye"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Canal principal</h4>
                            <span className="metric-value">{tendencias.canalPrincipal.label}</span>
                            <small>{tendencias.canalPrincipal.total} interacciones acumuladas</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-layer-group"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Cobertura de aulas</h4>
                            <span className="metric-value">{tendencias.coberturaAulas}%</span>
                            <small>aulas con movimiento en el ranking actual</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-folder-open"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Archivos por aula</h4>
                            <span className="metric-value">{tendencias.promedioArchivosPorAula}</span>
                            <small>promedio de materiales compartidos</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-reply"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Respuesta en foro</h4>
                            <span className="metric-value">{tendencias.intensidadForo}%</span>
                            <small>relación entre respuestas y mensajes</small>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          )}

          {activeMenuItem !== 'dashboard' && (
            <div className="content-placeholder">
              <div className="placeholder-icon">
                <i className={menuItems.find((item) => item.id === activeMenuItem)?.icon}></i>
              </div>
              <h2>Sección en Desarrollo</h2>
              <p>Esta sección estará disponible próximamente.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NewDashboard;
